import { Controller } from "@hotwired/stimulus"

// Drives one lifecycle-message template editor in Settings → Comms: live-renders
// an SMS / email-subject / email-body preview against sample merge values as the
// operator types, inserts {{tokens}} at the cursor, and resets a field back to
// the coded default. Each field carries its tokenized default (data-*-default) so
// an EMPTY field previews the default — exactly the runtime fallback behavior.
// Progressive enhancement: the form posts fine without any of this.
export default class extends Controller {
  static targets = [
    "smsInput", "smsPreview", "smsCount",
    "subjectInput", "subjectPreview",
    "bodyInput", "bodyPreview"
  ]
  static values = { sample: Object }

  connect() {
    this.lastField = null
    this.element.querySelectorAll("textarea, input[type=text]").forEach((el) => {
      el.addEventListener("focus", () => { this.lastField = el })
    })
    this.render()
  }

  render() {
    if (this.hasSmsInputTarget && this.hasSmsPreviewTarget) {
      const text = this.effective(this.smsInputTarget)
      this.smsPreviewTarget.textContent = this.merge(text) || "(empty)"
      this.updateCount(this.merge(text))
    }
    if (this.hasSubjectInputTarget && this.hasSubjectPreviewTarget) {
      this.subjectPreviewTarget.textContent = this.merge(this.effective(this.subjectInputTarget)) || "(no subject)"
    }
    if (this.hasBodyInputTarget && this.hasBodyPreviewTarget) {
      this.bodyPreviewTarget.textContent = this.merge(this.effective(this.bodyInputTarget)) || "(empty)"
    }
  }

  // The text that will actually be used: the operator's value, or the field's
  // tokenized default when they've left it blank (the server-side fallback).
  effective(el) {
    const value = (el.value || "").trim()
    return value.length ? el.value : (el.dataset.default || "")
  }

  // Insert a {{token}} at the cursor of the most recently focused field.
  insertField(event) {
    const token = `{{${event.currentTarget.dataset.field}}}`
    const el = this.lastField
    if (!el) return

    const start = el.selectionStart ?? el.value.length
    const end = el.selectionEnd ?? el.value.length
    el.value = el.value.slice(0, start) + token + el.value.slice(end)
    const caret = start + token.length
    el.setSelectionRange(caret, caret)
    el.focus()
    this.render()
  }

  // Clear an override field → it falls back to the coded default (shown live in
  // the preview). Persisted on the next save (a blank value removes the override).
  reset(event) {
    const channel = event.currentTarget.dataset.channel
    const map = {
      sms: this.hasSmsInputTarget ? this.smsInputTarget : null,
      email_subject: this.hasSubjectInputTarget ? this.subjectInputTarget : null,
      email_body: this.hasBodyInputTarget ? this.bodyInputTarget : null
    }
    const el = map[channel]
    if (el) {
      el.value = ""
      this.render()
    }
  }

  // Substitute known {{tokens}} with the sample values; unknown tokens stay
  // literal (mirrors the server-side Broadcasts::MergeFields contract).
  merge(text) {
    const sample = this.sampleValue || {}
    return (text || "").replace(/\{\{\s*([a-z_]+)\s*\}\}/gi, (match, key) => {
      const value = sample[key.toLowerCase()]
      return value === undefined ? match : value
    })
  }

  updateCount(text) {
    if (!this.hasSmsCountTarget) return
    const len = (text || "").length
    const segs = len <= 160 ? 1 : Math.ceil(len / 153)
    this.smsCountTarget.textContent = `${len} chars · ${segs} SMS ${segs === 1 ? "segment" : "segments"} (+ STOP hint appended)`
    this.smsCountTarget.classList.toggle("text-warning-600", len > 160)
    this.smsCountTarget.classList.toggle("text-secondary-500", len <= 160)
  }
}
